import { useState } from 'react';
import { Grid, Group, Select, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { ORBIT_DISABLED, ORBIT_GRAY } from '@/theme/colors';
import { LockedValue } from './LockedValue';

interface PanelSelectRowProps {
  label: string;
  value: string | undefined;
  options: readonly string[];
  /** Rejects if the write fails; the row stays in edit mode when it does. */
  onChange: (value: string) => Promise<void> | void;
  /** Shown when nothing is set yet — the row still renders, because it is editable. */
  placeholder?: string;
  /** Muted trailing text reconciling this value with another surface. */
  note?: string;
  /** Type to filter, for a long list such as the insurers (BAR-3079). */
  searchable?: boolean;
  /** Set once only the system may change the value: shown with a lock and this reason, not editable. */
  lockedReason?: string;
}

/**
 * An editable label-value row: click the value, pick from a list.
 *
 * Follows `AboutSection`'s `EditableGender` — click to reveal a `Select` that
 * opens immediately and closes on blur — so the two editable surfaces in this
 * panel behave the same way as the ones above them.
 *
 * Unlike `PanelRow`, this **renders even when empty**. An absent read-only row
 * means "this didn't happen"; an absent editable row would mean "you can't set
 * this", which is wrong — an unset Service is exactly the case a rep needs to
 * fix on the call.
 *
 * A value outside `options` is still displayed and still selectable away from.
 * Dropping it would make a renamed offering look like missing data.
 */
export function PanelSelectRow({
  label,
  value,
  options,
  onChange,
  placeholder = 'Not set',
  note,
  searchable,
  lockedReason,
}: PanelSelectRowProps) {
  const [isEditing, setIsEditing] = useState(false);

  // A stored value the list no longer contains still needs an entry, or Mantine
  // renders the Select as empty and the rep silently loses what was there.
  const data = value && !options.includes(value) ? [...options, value] : [...options];

  return (
    <Grid align="flex-start" style={{ width: '100%' }}>
      <Grid.Col span={3}>
        <Text style={{ fontSize: 13, lineHeight: '16px', color: ORBIT_GRAY }}>{label}</Text>
      </Grid.Col>
      <Grid.Col span={9}>
        {lockedReason ? (
          <LockedValue reason={lockedReason}>
            <Text
              style={{ fontSize: 14, lineHeight: '20px', color: value ? 'var(--mantine-color-black)' : ORBIT_DISABLED }}
            >
              {value ?? placeholder}
            </Text>
          </LockedValue>
        ) : isEditing ? (
          <Select
            data={data}
            value={value ?? null}
            onChange={(next) => {
              // No same-value guard: Mantine's Combobox does not fire onChange
              // when the already-selected option is picked again, so one here
              // would be unreachable. Verified by mutation — removing a guard
              // changed no test outcome.
              if (!next) {
                setIsEditing(false);
                return;
              }

              // Close only once the write resolves. Closing optimistically made
              // a failed save look exactly like a successful one: the row shut,
              // then reverted to the old value on the next render with nothing
              // said. On failure the row stays open holding what the rep chose.
              void Promise.resolve(onChange(next)).then(
                () => setIsEditing(false),
                () => undefined
              );
            }}
            onBlur={() => setIsEditing(false)}
            searchable={searchable}
            size="xs"
            radius="md"
            autoFocus
            defaultDropdownOpened
            comboboxProps={{ withinPortal: true }}
            styles={{ input: { height: 20, minHeight: 20, fontSize: 13 } }}
            style={{ width: '100%' }}
          />
        ) : (
          <Group
            gap={5}
            wrap="nowrap"
            onClick={() => setIsEditing(true)}
            style={{ cursor: 'pointer', display: 'inline-flex' }}
          >
            <Text
              style={{
                fontSize: 14,
                lineHeight: '20px',
                color: value ? 'var(--mantine-color-black)' : ORBIT_DISABLED,
              }}
            >
              {value ?? placeholder}
            </Text>
            <IconChevronDown size={11} stroke={1.6} style={{ color: ORBIT_GRAY, flexShrink: 0 }} />
            {note && (
              <Text style={{ fontSize: 13, lineHeight: '20px', color: ORBIT_GRAY, whiteSpace: 'nowrap' }}>{note}</Text>
            )}
          </Group>
        )}
      </Grid.Col>
    </Grid>
  );
}
